import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { riskLayersAtom } from '@app/risk-migration/atoms/riskLayers.atoms'
import {
  activeCapitalSetIndexAtom,
  activeScenarioSetIndexAtom,
  activeWorkspaceAtom,
  activeWorkspaceLayerIdsAtom,
  getCapitalSetOptions,
  getScenarioSetOptions,
  hazardOptionStateAtom,
  resolveSetIndex,
  riskMapInstanceAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { buildHazardOptionState } from '@app/risk-migration/hazardOptions'
import { useRiskFilterState } from '@app/risk-migration/hooks/useRiskFilterState'
import { useRiskMapNavigation } from '@app/risk-migration/hooks/useRiskMapNavigation'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import {
  getWorkspaceSetLayerIds,
  isElementLayer,
  mapLayerKey,
  resolveVersionedLayerId,
} from '@app/modules/Risk/Workspaces/workspace.utils'
import type { DialogData, RiskWorkspace } from '@app/types'
import * as turf from '@turf/turf'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useMemo } from 'react'
import {
  getCurrentContexts,
  getCurrentElements,
  getCurrentHazards,
  getIndexedLayers,
} from '../utils/Workspaces.utils'
import { useFetchRegions } from '@app/hooks/useFetchRegions'

export const useWorkspaceLoader = () => {
  const { fitToBounds } = useRiskMapNavigation()
  const map = useAtomValue(riskMapInstanceAtom)
  const [riskLayers, setRiskLayers] = useAtom(riskLayersAtom)
  const setHazardOptionState = useSetAtom(hazardOptionStateAtom)
  const [activeWorkspace, setActiveWorkspace] = useAtom(activeWorkspaceAtom)
  const [activeWorkspaceLayerIds, setActiveWorkspaceLayerIds] = useAtom(activeWorkspaceLayerIdsAtom)
  const [activeScenarioSetIndex, setActiveScenarioSetIndex] = useAtom(activeScenarioSetIndexAtom)
  const [activeCapitalSetIndex, setActiveCapitalSetIndex] = useAtom(activeCapitalSetIndexAtom)
  const { useFeatureFlag } = useFeatureFlags()
  const workspaceV2Enabled = useFeatureFlag(FEATURE_FLAGS.WORKSPACE_V2)

  const { setRegionMasks } = useRiskFilterState()
  const layerData = useLayerDataFetcher()
  const { data: availableRegions = [] } = useFetchRegions()
  const { liveLayers, setActiveLiveLayers } = useLiveContext()

  const { elementsData, scenariosData, contextsData } = useMemo(
    () =>
      layerData
        ? getIndexedLayers(layerData)
        : { elementsData: {}, scenariosData: {}, contextsData: {} },
    [layerData],
  )

  const allRelevantLayers = useMemo(() => {
    return {
      ...getCurrentElements(elementsData),
      ...getCurrentContexts(contextsData),
      ...getCurrentHazards(scenariosData),
    }
  }, [elementsData, scenariosData, contextsData])

  // Element layer ids by catalog type, as some don't start with `e` (e.g. `iso48`)
  const elementLayerIds = useMemo(
    () =>
      ((layerData as DialogData | null)?.layers ?? []).filter(isElementLayer).map(({ id }) => id),
    [layerData],
  )

  // Resolves saved ids to the available (versioned) ones, dropping any that don't exist
  const validLayerIds = useCallback(
    (ids: string[] = []) => {
      const availableIds = Object.keys(allRelevantLayers)
      return [...new Set(ids.flatMap((id) => resolveVersionedLayerId(id, availableIds) ?? []))]
    },
    [allRelevantLayers],
  )

  const toMapLayers = useCallback(
    (ids: string[]) => ids.flatMap((id) => (allRelevantLayers[id] ? [allRelevantLayers[id]] : [])),
    [allRelevantLayers],
  )

  // Valid layer ids for the selected sets; v1 workspaces and the legacy flow use workspace.layers
  const getLayerIdsForSets = useCallback(
    (
      workspace: RiskWorkspace,
      scenarioIndex: number | null,
      capitalIndex: number | null,
    ): RiskWorkspace['layers'] => {
      const ids = workspaceV2Enabled
        ? getWorkspaceSetLayerIds(
            workspace,
            scenarioIndex,
            capitalIndex,
            Object.keys(scenariosData),
            Object.keys(elementsData),
            elementLayerIds,
          )
        : workspace.layers
      return {
        assets: validLayerIds(ids.assets),
        scenarios: validLayerIds(ids.scenarios),
        contexts: validLayerIds(ids.contexts),
        live: ids.live ?? [],
      }
    },
    [workspaceV2Enabled, scenariosData, elementsData, elementLayerIds, validLayerIds],
  )

  const loadWorkspace = useCallback(
    async (workspace: RiskWorkspace) => {
      if (!workspace || !map) {
        throw new Error('No Workspace selected or map unavailable')
      }

      // create a shallow copy to avoid mutating the original
      const workspaceCopy: RiskWorkspace = {
        ...workspace,
        layers: {
          ...workspace.layers,
        },
      }

      // remove any invalid layers from the loaded workspace
      workspaceCopy.layers.assets = validLayerIds(workspaceCopy.layers.assets)
      workspaceCopy.layers.scenarios = validLayerIds(workspaceCopy.layers.scenarios)
      workspaceCopy.layers.contexts = validLayerIds(workspaceCopy.layers.contexts)

      // v2 loads only the first scenario and capital sets, v1 loads everything
      const layerIdsToLoad = getLayerIdsForSets(
        workspaceCopy,
        resolveSetIndex(getScenarioSetOptions(workspaceCopy), null),
        resolveSetIndex(getCapitalSetOptions(workspaceCopy), null),
      )

      const layersToLoad = toMapLayers([
        ...layerIdsToLoad.assets,
        ...layerIdsToLoad.scenarios,
        ...layerIdsToLoad.contexts,
      ])

      // alongside the regular layers (matching what AddLayersDialog does).
      const savedLiveLayerIds = workspaceCopy.layers.live ?? []
      const liveLayersToRestore = savedLiveLayerIds.length
        ? liveLayers.filter((l) => savedLiveLayerIds.includes(l.id))
        : []

      // Resolve the saved region codes against the available regions list so
      // we can pass full RegionOption objects to the layer pipeline. If saved
      // codes don't resolve (e.g. regions list not loaded yet) we end up with
      // an empty array, which clears any active region mask — matching the
      // semantics for a workspace saved without regions.
      const savedRegionCodes = workspaceCopy.regions ?? []
      const regionsToRestore = savedRegionCodes.length
        ? availableRegions.filter((r) => savedRegionCodes.includes(r.region))
        : []
      if (savedRegionCodes.length && !regionsToRestore.length) {
        // eslint-disable-next-line no-console
        console.warn(
          'Workspace has saved regions but none match the available regions list — skipping region restore',
        )
      }

      // Pass regions AND draw areas into the single layer pass so the first
      // visible-asset computation is already filtered — avoids loading the
      // whole element layer then re-filtering once draw areas hit Redux.
      setRegionMasks(regionsToRestore.length ? regionsToRestore : null)
      const layers = [...layersToLoad, ...liveLayersToRestore]
      setRiskLayers(layers)
      setHazardOptionState(buildHazardOptionState(layers, 'all'))

      // Frame the restored regions, mirroring the add-region UX in
      // RegionFilter (combined bbox of all selected regions).
      if (regionsToRestore.length) {
        const bbox = turf.bbox(
          turf.points(
            regionsToRestore.flatMap(({ min_x, min_y, max_x, max_y }) => [
              [max_x, max_y],
              [min_x, min_y],
            ]),
          ),
        )
        fitToBounds({ boundingBox: [bbox[0], bbox[1], bbox[2], bbox[3]] })
      }

      // here we just (re)draw the polygon outline on the map.

      if (liveLayersToRestore.length) {
        setActiveLiveLayers(liveLayersToRestore)
      }

      // workspace when it detects layers have changed.
      setActiveWorkspace(workspaceCopy)
      setActiveWorkspaceLayerIds(layerIdsToLoad)

      return workspaceCopy
    },
    [
      map,
      liveLayers,
      availableRegions,
      setRegionMasks,
      setRiskLayers,
      setHazardOptionState,
      setActiveWorkspace,
      setActiveWorkspaceLayerIds,
      validLayerIds,
      toMapLayers,
      getLayerIdsForSets,
      fitToBounds,
      setActiveLiveLayers,
    ],
  )

  // Swaps the previous set's layers for the new set's, keeping any other layers on the map
  const switchWorkspaceSet = useCallback(
    (setType: 'scenario' | 'capital', index: number) => {
      if (!activeWorkspace) return

      const scenarioIndex = setType === 'scenario' ? index : activeScenarioSetIndex
      const capitalIndex = setType === 'capital' ? index : activeCapitalSetIndex
      if (setType === 'scenario') setActiveScenarioSetIndex(index)
      else setActiveCapitalSetIndex(index)

      const key = setType === 'scenario' ? 'scenarios' : 'assets'
      const layerType = setType === 'scenario' ? 'hazard' : 'asset'
      const nextIds = getLayerIdsForSets(activeWorkspace, scenarioIndex, capitalIndex)[key]
      const prevIds = new Set(activeWorkspaceLayerIds?.[key] ?? [])

      const removed = riskLayers.filter(
        (layer) => layer.layerType === layerType && prevIds.has(mapLayerKey(layer)),
      )
      const kept = riskLayers.filter((layer) => !removed.includes(layer))
      const keptKeys = new Set(kept.map(mapLayerKey))
      const added = toMapLayers(nextIds).filter((layer) => !keptKeys.has(mapLayerKey(layer)))

      setRiskLayers([...kept, ...added])

      if (setType === 'scenario') {
        const keptHazardIds = new Set(kept.map((layer) => layer.hazard_id))
        setHazardOptionState((prev) => {
          const next = { ...prev }
          removed.forEach(({ hazard_id }) => {
            if (hazard_id !== undefined && !keptHazardIds.has(hazard_id)) delete next[hazard_id]
          })
          Object.entries(buildHazardOptionState(added, 'all')).forEach(([hazardId, options]) => {
            next[hazardId] ??= options
          })
          return next
        })
      }

      setActiveWorkspaceLayerIds((prev) => (prev ? { ...prev, [key]: nextIds } : prev))
    },
    [
      activeWorkspace,
      activeScenarioSetIndex,
      activeCapitalSetIndex,
      setActiveScenarioSetIndex,
      setActiveCapitalSetIndex,
      activeWorkspaceLayerIds,
      riskLayers,
      getLayerIdsForSets,
      toMapLayers,
      setRiskLayers,
      setHazardOptionState,
      setActiveWorkspaceLayerIds,
    ],
  )

  return { loadWorkspace, switchWorkspaceSet }
}
